import React, { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { format, differenceInDays } from 'date-fns';
import { RefreshCw, Loader2, Zap, Users, Receipt, Search, X } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Skeleton } from '../ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Save as SaveIcon } from 'lucide-react';
import api from '../../lib/api';

const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(v) || 0);

// ── Service Labor Modal ──
function ServiceLaborModal({ open, onOpenChange, invoice, onResync }) {
  const assignees = Array.isArray(invoice?.staff_data) ? invoice.staff_data : [];
  const [syncing, setSyncing] = useState(false);
  const totalHours = assignees.reduce((s, a) => s + (a.assigned_hours || 0), 0);
  const totalCost = assignees.reduce((s, a) => s + (a.cost || 0), 0);

  const handleResync = async () => {
    if (!invoice?.id) return;
    setSyncing(true);
    try { await onResync(invoice.id); toast.success('Labor synced'); }
    catch { toast.error('Sync failed'); }
    finally { setSyncing(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle data-role="heading">Labor: {invoice?.client || invoice?.task_name}</DialogTitle>
        </DialogHeader>
        <div className="py-3">
          {assignees.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No tracked time. Click Re-sync.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs font-semibold uppercase">Name</TableHead>
                  <TableHead className="text-right text-xs font-semibold uppercase">Hours</TableHead>
                  <TableHead className="text-right text-xs font-semibold uppercase">Rate</TableHead>
                  <TableHead className="text-right text-xs font-semibold uppercase">Cost</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assignees.map((a, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-sm font-medium">{a.username || 'Unknown'}</TableCell>
                    <TableCell className="text-right text-sm" data-role="mono">{(a.assigned_hours ?? 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right text-sm" data-role="mono">{fmt(a.pay_rate ?? 0)}/hr</TableCell>
                    <TableCell className="text-right text-sm font-semibold" data-role="mono">{fmt(a.cost ?? 0)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-t-2">
                  <TableCell className="text-sm font-semibold">Total</TableCell>
                  <TableCell className="text-right text-sm font-semibold" data-role="mono">{totalHours.toFixed(2)}</TableCell>
                  <TableCell />
                  <TableCell className="text-right text-sm font-bold" data-role="mono">{fmt(totalCost)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={handleResync} disabled={syncing}>
            {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Re-sync
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Service Expense Modal ──
function ServiceExpenseModal({ open, onOpenChange, invoice, onSave }) {
  const [se, setSe] = useState({ labor_cost: 0, one_time_costs: 0, misc_costs: 0 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && invoice) {
      const raw = invoice.service_expenses || {};
      setSe({
        labor_cost: raw.labor_cost ?? 0,
        one_time_costs: raw.one_time_costs ?? 0,
        misc_costs: raw.misc_costs ?? 0,
      });
    }
  }, [open, invoice]);

  const totalCost = (parseFloat(se.labor_cost) || 0) + (parseFloat(se.one_time_costs) || 0) + (parseFloat(se.misc_costs) || 0);
  const invoiced = parseFloat(invoice?.amount) || 0;
  const profit = invoiced - totalCost;
  const margin = invoiced > 0 ? (profit / invoiced * 100).toFixed(1) : 0;
  const hours = parseFloat(invoice?.hours) || 0;
  const profitRate = hours > 0 ? profit / hours : 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(invoice.id, {
        labor_cost: parseFloat(se.labor_cost) || 0,
        one_time_costs: parseFloat(se.one_time_costs) || 0,
        misc_costs: parseFloat(se.misc_costs) || 0,
      });
      onOpenChange(false);
    } catch {} finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 gap-0 flex flex-col" style={{ maxHeight: '80vh' }}>
        <DialogHeader className="px-6 pt-5 pb-3">
          <DialogTitle className="flex items-center gap-2" data-role="heading">
            <Receipt className="h-5 w-5 text-primary" />
            Service Expense Ledger
          </DialogTitle>
          <p className="text-xs text-muted-foreground">{invoice?.client || invoice?.task_name}</p>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-4">
          <div className="border rounded-lg p-4 space-y-3">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-foreground">Costs</h3>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Internal Labor (ClickUp)</label>
              <Input type="text" value={se.labor_cost} disabled className="h-8 text-sm text-right bg-muted/50 text-muted-foreground" data-role="mono" />
              <p className="text-[10px] text-muted-foreground mt-0.5">Auto-calculated from time entries</p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">One-Time Costs</label>
              <Input type="text" inputMode="decimal" value={se.one_time_costs}
                onChange={(e) => setSe(prev => ({ ...prev, one_time_costs: e.target.value }))}
                className="h-8 text-sm text-right" data-role="mono" />
              <p className="text-[10px] text-muted-foreground mt-0.5">Tools, software, or ticket-specific costs</p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Miscellaneous</label>
              <Input type="text" inputMode="decimal" value={se.misc_costs}
                onChange={(e) => setSe(prev => ({ ...prev, misc_costs: e.target.value }))}
                className="h-8 text-sm text-right" data-role="mono" />
              <p className="text-[10px] text-muted-foreground mt-0.5">Any other expenses</p>
            </div>
          </div>
          <div className="border rounded-lg p-4 space-y-2">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-foreground">Analysis</h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="bg-muted/50 rounded px-2.5 py-2">
                <span className="text-muted-foreground block">Total Cost</span>
                <span className="font-semibold" data-role="mono">{fmt(totalCost)}</span>
              </div>
              <div className="bg-muted/50 rounded px-2.5 py-2">
                <span className="text-muted-foreground block">Invoiced</span>
                <span className="font-semibold" data-role="mono">{fmt(invoiced)}</span>
              </div>
              <div className="bg-muted/50 rounded px-2.5 py-2">
                <span className="text-muted-foreground block">Net Profit</span>
                <span className={`font-semibold ${profit >= 0 ? 'text-emerald-700' : 'text-red-600'}`} data-role="mono">{fmt(profit)}</span>
              </div>
              <div className="bg-muted/50 rounded px-2.5 py-2">
                <span className="text-muted-foreground block">Margin</span>
                <span className={`font-semibold ${parseFloat(margin) >= 50 ? 'text-emerald-700' : 'text-red-600'}`} data-role="mono">{margin}%</span>
              </div>
            </div>
            {hours > 0 && (
              <div className="text-[10px] text-muted-foreground pt-1">
                Profit rate: <span className="font-semibold" data-role="mono">{fmt(profitRate)}/hr</span> after all costs
              </div>
            )}
          </div>
        </div>
        <div className="flex-shrink-0 border-t bg-card px-6 py-3 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <SaveIcon className="h-4 w-4 mr-1" />}Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Main ──
export default function ServiceCommandCenter({ onOpenStaff }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState('all');
  const [days, setDays] = useState(30);
  const [search, setSearch] = useState('');
  const [prodEmails, setProdEmails] = useState(new Set());
  const [laborInvoice, setLaborInvoice] = useState(null);
  const [expenseInvoice, setExpenseInvoice] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = { days };
      if (filter !== 'all') params.status = filter;
      const res = await api.get('/api/ops/services', { params });
      setItems(res.data.data || []);
    } catch { toast.error('Failed to load services'); }
    finally { setLoading(false); }
  }, [days, filter]);

  const loadStaffRoles = useCallback(async () => {
    try {
      const res = await api.get('/api/ops/staff');
      const s = new Set();
      (res.data.data || []).forEach(m => {
        if (m.role === 'Prod' || m.role === 'Both') {
          s.add(m.email.toLowerCase());
          s.add((m.name || '').toLowerCase());
        }
      });
      setProdEmails(s);
    } catch {}
  }, []);

  useEffect(() => { load(); loadStaffRoles(); }, [load, loadStaffRoles]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/api/ops/sync-payments');
      toast.success(`Synced: ${res.data.synced} paid of ${res.data.checked} checked`);
      load();
    } catch { toast.error('Sync failed'); }
    finally { setSyncing(false); }
  };

  const handleLaborResync = async (id) => {
    const res = await api.post(`/api/ops/services/${id}/sync-labor`);
    setItems(prev => prev.map(i => i.id === id ? res.data.data : i));
    setLaborInvoice(res.data.data);
  };

  const handleSaveExpenses = async (id, expenses) => {
    try {
      const res = await api.patch(`/api/ops/services/${id}/expenses`, { service_expenses: expenses });
      setItems(prev => prev.map(i => i.id === id ? res.data.data : i));
      toast.success('Expenses saved');
    } catch { toast.error('Failed to save'); throw new Error(); }
  };

  const formatDate = (d) => {
    if (!d) return '\u2014';
    try { return format(new Date(d), 'MM/dd/yy'); } catch { return '\u2014'; }
  };

  // Apply search filter (fuzzy, case-insensitive)
  const searchLower = search.toLowerCase().trim();
  const filtered = searchLower
    ? items.filter(inv => {
        const fields = [
          inv.client, inv.client_email, inv.task_name, inv.task_list,
          ...(Array.isArray(inv.staff_data) ? inv.staff_data.map(s => s.username || s.email || '') : []),
        ];
        return fields.some(f => (f || '').toLowerCase().includes(searchLower));
      })
    : items;

  // Top producers from filtered results
  const staffRevenue = {};
  filtered.forEach(inv => {
    const staff = Array.isArray(inv.staff_data) ? inv.staff_data : [];
    const pp = staff.length > 0 ? (parseFloat(inv.amount) || 0) / staff.length : 0;
    staff.forEach(s => {
      const email = (s.email || '').toLowerCase();
      const name = s.username || s.email || 'Unknown';
      if (prodEmails.has(email) || prodEmails.has(name.toLowerCase())) {
        staffRevenue[name] = (staffRevenue[name] || 0) + pp;
      }
    });
  });
  const topContributors = Object.entries(staffRevenue).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const totalRevenue = filtered.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const totalCosts = filtered.reduce((s, i) => {
    const se = i.service_expenses || {};
    return s + (parseFloat(se.labor_cost) || 0) + (parseFloat(se.one_time_costs) || 0) + (parseFloat(se.misc_costs) || 0);
  }, 0);
  const totalNetProfit = totalRevenue - totalCosts;
  const avgMargin = totalRevenue > 0 ? (totalNetProfit / totalRevenue * 100) : 0;
  const paidCount = filtered.filter(i => i.payment_status === 'paid').length;

  return (
    <>
      <Card className="shadow-[var(--shadow-1)]">
        <CardHeader className="pb-2 pt-4 px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold tracking-tight" data-role="heading">Services</h2>
              <Badge variant="secondary" className="text-xs">{filtered.length} invoices</Badge>
              <Badge variant="outline" className="text-xs text-emerald-700 border-emerald-200">{paidCount} paid</Badge>
              {onOpenStaff && (
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground" onClick={onOpenStaff} title="Manage staff roles">
                  <Users className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search client..."
                  className="text-xs border rounded pl-6 pr-6 py-1 bg-background w-[140px] focus:w-[180px] transition-all focus:outline-none focus:ring-1 focus:ring-ring"
                />
                {search && (
                  <button onClick={() => setSearch('')} className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted text-muted-foreground">
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
              <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="text-xs border rounded px-2 py-1 bg-background">
                <option value={7}>7 days</option>
                <option value={30}>30 days</option>
                <option value={90}>90 days</option>
                <option value={365}>1 year</option>
              </select>
              {['all', 'sent', 'paid'].map(f => (
                <Button key={f} variant={filter === f ? 'default' : 'outline'} size="sm" className="text-xs h-7 px-2" onClick={() => setFilter(f)}>
                  {f === 'all' ? 'All' : f === 'sent' ? 'Pending' : 'Paid'}
                </Button>
              ))}
              <Button variant="outline" size="sm" className="text-xs h-7" onClick={handleSync} disabled={syncing}>
                {syncing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}Sync
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-0 pb-0">
          {/* Top Producers */}
          {topContributors.length > 0 && (
            <div className="mx-4 sm:mx-6 mb-3 p-2.5 rounded-lg bg-amber-50/60 border border-amber-200/60 flex items-center gap-3 overflow-x-auto">
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <Zap className="h-4 w-4 text-amber-500" />
                <span className="text-[11px] font-semibold text-amber-800 uppercase tracking-wide">Top Producers</span>
              </div>
              <div className="flex items-center gap-2 overflow-x-auto">
                {topContributors.map(([name, rev], i) => (
                  <div key={name} className="flex items-center gap-1.5 bg-white/80 rounded-md px-2.5 py-1 flex-shrink-0 border border-amber-200/40">
                    <span className="text-[10px] font-bold text-amber-600 w-4">{i + 1}.</span>
                    <span className="text-xs font-medium text-foreground">{name}</span>
                    <span className="text-xs font-bold text-emerald-700" data-role="mono">{fmt(rev)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {loading ? (
            <div className="p-6 space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center">
              <p className="text-sm text-muted-foreground">
                {search ? `No results for "${search}"` : 'No approved invoices in this period'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-[90px] text-xs font-semibold uppercase tracking-wide text-muted-foreground">Date</TableHead>
                    <TableHead className="min-w-[130px] text-xs font-semibold uppercase tracking-wide text-muted-foreground">Client</TableHead>
                    <TableHead className="min-w-[110px] text-xs font-semibold uppercase tracking-wide text-muted-foreground">Team</TableHead>
                    <TableHead className="w-[100px] text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">Amount</TableHead>
                    <TableHead className="w-[90px] text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">Cost</TableHead>
                    <TableHead className="w-[80px] text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">$/Hr</TableHead>
                    <TableHead className="w-[90px] text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">Net Profit</TableHead>
                    <TableHead className="w-[75px] text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">Margin</TableHead>
                    <TableHead className="w-[70px] text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">Velocity</TableHead>
                    <TableHead className="w-[70px] text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(inv => {
                    const amount = parseFloat(inv.amount) || 0;
                    const hours = parseFloat(inv.hours) || 0;
                    const se = inv.service_expenses || {};
                    const laborCost = parseFloat(se.labor_cost) || 0;
                    const oneTimeCost = parseFloat(se.one_time_costs) || 0;
                    const miscCost = parseFloat(se.misc_costs) || 0;
                    const totalCost = laborCost + oneTimeCost + miscCost;
                    const profit = amount - totalCost;
                    const realizedRate = hours > 0 ? amount / hours : 0;
                    const rateColor = realizedRate >= 150 ? 'text-emerald-700' : realizedRate < 60 ? 'text-red-600' : 'text-foreground';
                    const margin = amount > 0 ? ((profit / amount) * 100).toFixed(0) : 0;

                    let velocity = '\u2014';
                    if (inv.payment_status === 'paid' && inv.date_approved && inv.date_paid) {
                      try { velocity = `${differenceInDays(new Date(inv.date_paid), new Date(inv.date_approved))}d`; } catch {}
                    }

                    const staffList = Array.isArray(inv.staff_data) ? inv.staff_data : [];
                    const staffNames = staffList.map(s => s.username || s.email || '').filter(Boolean).join(', ');

                    return (
                      <TableRow key={inv.id} className="hover:bg-[var(--row-hover)] transition-colors duration-150">
                        <TableCell><span className="text-sm" data-role="mono">{formatDate(inv.date_approved || inv.updated_at)}</span></TableCell>
                        <TableCell>
                          {inv.clickup_task_id && !inv.clickup_task_id.startsWith('merged_') ? (
                            <a href={`https://app.clickup.com/t/${inv.clickup_task_id}`} target="_blank" rel="noopener noreferrer"
                              className="text-sm font-medium text-primary hover:underline truncate block max-w-[150px]">
                              {inv.client || inv.task_list || '\u2014'}
                            </a>
                          ) : (
                            <span className="text-sm font-medium truncate block max-w-[150px]">{inv.client || '\u2014'}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {staffNames ? (
                            <button onClick={() => setLaborInvoice(inv)}
                              className="text-sm text-primary hover:underline truncate block max-w-[120px] text-left">
                              <span className="inline-flex items-center gap-1">
                                <Users className="h-3 w-3 flex-shrink-0" />
                                {staffNames}
                              </span>
                            </button>
                          ) : (
                            <button onClick={() => setLaborInvoice(inv)}
                              className="text-sm text-muted-foreground hover:text-primary text-left">
                              <span className="inline-flex items-center gap-1">
                                <Users className="h-3 w-3 flex-shrink-0" />
                                —
                              </span>
                            </button>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <span className="text-sm font-semibold" data-role="mono">{fmt(amount)}</span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs">Margin: {margin}% ({fmt(profit)} profit)</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                        <TableCell className="text-right">
                          <button onClick={() => setExpenseInvoice(inv)}
                            className="text-sm font-medium text-primary hover:underline cursor-pointer inline-flex items-center gap-1 ml-auto"
                            title="Open expense ledger" data-role="mono">
                            <Receipt className="h-3 w-3 flex-shrink-0" />
                            {fmt(totalCost)}
                          </button>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`text-sm font-medium ${rateColor}`} data-role="mono">
                            {realizedRate > 0 ? `$${realizedRate.toFixed(0)}` : '\u2014'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={`text-sm font-semibold ${profit >= 0 ? 'text-foreground' : 'text-red-600'}`} data-role="mono">
                            {fmt(profit)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {(() => {
                            const m = parseFloat(margin);
                            const cls = m >= 70 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : m >= 50 ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                              : 'bg-red-50 text-red-700 border-red-200';
                            return <Badge variant="outline" className={`${cls} text-xs tabular-nums`}>{m}%</Badge>;
                          })()}
                        </TableCell>
                        <TableCell className="text-right"><span className="text-sm" data-role="mono">{velocity}</span></TableCell>
                        <TableCell>
                          {inv.payment_status === 'paid' ? (
                            <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">Paid</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">Sent</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {filtered.length > 0 && (
            <div className="px-4 sm:px-6 py-2 border-t bg-muted/30 flex items-center justify-between text-xs text-muted-foreground">
              <div className="flex items-center gap-4">
                <span>
                  Revenue{search ? ` for "${search}"` : ''}: <span className="font-semibold text-foreground" data-role="mono">{fmt(totalRevenue)}</span>
                </span>
                <span>
                  Net Profit: <span className={`font-semibold ${totalNetProfit >= 0 ? 'text-emerald-700' : 'text-red-600'}`} data-role="mono">{fmt(totalNetProfit)}</span>
                </span>
                <span>
                  Avg Margin: <span className={`font-semibold ${avgMargin >= 70 ? 'text-emerald-700' : avgMargin >= 50 ? 'text-yellow-700' : 'text-red-600'}`} data-role="mono">{avgMargin.toFixed(1)}%</span>
                </span>
              </div>
              <span>{paidCount}/{filtered.length} paid</span>
            </div>
          )}
        </CardContent>
      </Card>

      <ServiceLaborModal
        open={!!laborInvoice}
        onOpenChange={(o) => { if (!o) setLaborInvoice(null); }}
        invoice={laborInvoice}
        onResync={handleLaborResync}
      />
      <ServiceExpenseModal
        open={!!expenseInvoice}
        onOpenChange={(o) => { if (!o) setExpenseInvoice(null); }}
        invoice={expenseInvoice}
        onSave={handleSaveExpenses}
      />
    </>
  );
}
